import { forwardRef, useEffect, useRef } from 'react'
import type * as React from 'react'
import type { JSX } from 'react'

import {
  Badge,
  Button,
  IconButton,
  PanelRightCloseIcon,
  PanelRightOpenIcon,
  Tooltip,
} from '@buffer-mono/popcorn'

type UndatedDraftsToggleProps = {
  variant: 'wide' | 'narrow'
  isOpen: boolean
  label: string
  tooltip: string
  controlsId?: string
  count?: number
  className?: string
  children?: React.ReactNode
  onToggle: () => void
}

export const UndatedDraftsToggle = forwardRef<
  HTMLButtonElement,
  UndatedDraftsToggleProps
>(
  (
    {
      variant,
      isOpen,
      label,
      tooltip,
      controlsId,
      count,
      className,
      children,
      onToggle,
    },
    forwardedRef,
  ): JSX.Element => {
    const icon = isOpen ? <PanelRightCloseIcon /> : <PanelRightOpenIcon />

    return (
      <Tooltip content={tooltip}>
        {variant === 'wide' ? (
          <Button
            ref={forwardedRef}
            size="medium"
            variant="tertiary"
            aria-label={label}
            aria-expanded={isOpen}
            aria-controls={controlsId}
            onClick={onToggle}
            className={className}
          >
            {icon}
            {children}
            {typeof count === 'number' && count > 0 && (
              <Badge size="small">{count}</Badge>
            )}
          </Button>
        ) : (
          <IconButton
            ref={forwardedRef}
            variant="tertiary"
            label={label}
            aria-expanded={isOpen}
            aria-controls={controlsId}
            onClick={onToggle}
            className={className}
          >
            {icon}
          </IconButton>
        )}
      </Tooltip>
    )
  },
)

UndatedDraftsToggle.displayName = 'UndatedDraftsToggle'

export function useUndatedDraftsToggleFocusReturn(isOpen: boolean): {
  wideRef: React.RefObject<HTMLButtonElement>
  narrowRef: React.RefObject<HTMLButtonElement>
} {
  const wideRef = useRef<HTMLButtonElement>(null)
  const narrowRef = useRef<HTMLButtonElement>(null)
  const wasOpen = useRef(isOpen)

  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      const toggleButtons = [wideRef.current, narrowRef.current]
      const visibleToggleButton = toggleButtons.find((button) => {
        if (!button) {
          return false
        }

        return (
          button.getClientRects().length > 0 &&
          window.getComputedStyle(button).visibility !== 'hidden'
        )
      })

      const fallbackToggleButton = toggleButtons.find(
        (button): button is HTMLButtonElement => Boolean(button),
      )
      const toggleButtonToFocus = visibleToggleButton ?? fallbackToggleButton
      toggleButtonToFocus?.focus()
    }

    wasOpen.current = isOpen
  }, [isOpen])

  return { wideRef, narrowRef }
}
